<template>
  <BaseDrawer :is-open="isOpen" shrink @close="handleClose">
    <template #header>
      <div
        class="flex flex-1 items-center justify-between px-6 text-error"
      >
        <IconExclamationTriangleFilled class="size-8" />
        <p class="uppercase">Service logistics login</p>
        <IconExclamationTriangleFilled class="size-8" />
      </div>
    </template>
    <form
      class="mt-4 flex h-full flex-col items-center justify-end pb-6"
      @submit="onSubmit"
    >
      <PinInput
        :model-value="formValues.pin"
        @update:model-value="setFieldValue('pin', $event)"
      />
    </form>
  </BaseDrawer>
</template>

<script setup lang="ts">
import z from "zod"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import { logInAsServiceLogistics } from "@/services/logInAsServiceLogistics"
import { ApiError } from "@/services/api"
import { IconExclamationTriangleFilled } from "@/icons"
import { useConfirmDialog } from "@vueuse/core"

const { startLoading, stopLoading } =
  usePageBlockingLoading()

const {
  reveal,
  isRevealed: isOpen,
  confirm,
  cancel,
  onReveal,
} = useConfirmDialog()

defineExpose({
  reveal,
})

onReveal(() => {
  handleFormReset()
})

const handleClose = () => cancel()

const pinFormSchema = z.object({
  pin: z
    .string()
    .length(4, { message: "Pin must be 4 characters long" })
    .regex(/\d+/, {
      message: "Pin must only contain numeric characters",
    }),
})

const {
  handleSubmit,
  handleReset: handleFormReset,
  setFieldValue,
  values: formValues,
} = useForm({
  validationSchema: toTypedSchema(pinFormSchema),
  initialValues: {
    pin: "",
  },
})

const onSubmit = handleSubmit(
  async (submittedFormValues) => {
    startLoading()

    try {
      await logInAsServiceLogistics(submittedFormValues)
      confirm()
    } catch (error) {
      handleFormReset()
      if (
        error instanceof ApiError &&
        error.code === "pin-incorrect"
      ) {
        toast.error("PIN incorrect. Please try again.")
      } else {
        toast.error("Something went wrong")
      }
    } finally {
      stopLoading()
    }
  }
)

const pinIsFullyInput = computed(
  () => formValues.pin?.length === 4
)

whenever(
  () => pinIsFullyInput.value,
  () => onSubmit()
)
</script>
